'use client';

import dynamic from 'next/dynamic';
import Link from '~/(tenant)/_components/tenant-link';

import { PersonalAccountDropdown } from '@kit/accounts/personal-account-dropdown';
import { useSignOut } from '@kit/supabase/hooks/use-sign-out';
import { If } from '@kit/ui/if';
import { Trans } from '@kit/ui/trans';

import featuresFlagConfig from '~/config/feature-flags.config';
import pathsConfig from '~/config/paths.config';
import { UserAccountDropdown } from '~/(tenant)/_components/user-account-dropdown';
import { JWTUserData } from '@kit/supabase/types';
import { useSession, useUserRole, useTeamAccount } from '~/lib/hooks/use-header-auth';

const ModeToggle = dynamic(() =>
  import('@kit/ui/mode-toggle').then((mod) => ({
    default: mod.ModeToggle,
  })),
);

const MobileModeToggle = dynamic(() =>
  import('@kit/ui/mobile-mode-toggle').then((mod) => ({
    default: mod.MobileModeToggle,
  })),
);

const paths = {
  home: pathsConfig.app.home,
  bookCourt: '/book',
};

const features = {
  enableThemeToggle: featuresFlagConfig.enableThemeToggle,
};

interface DeemPadelHeaderAccountSectionProps {
  onNavigate?: () => void;
}

export function DeemPadelHeaderAccountSection({
  onNavigate,
}: DeemPadelHeaderAccountSectionProps) {
  const session = useSession();
  const userRole = useUserRole(session.data?.user?.id);
  const teamAccount = useTeamAccount(session.data?.user?.id);
  const signOut = useSignOut({
    onSuccess: () => window.location.reload(),
  });

  if (session.data) {
    const role = userRole.data || 'guest';

    // Owner/Member roles - show admin access
    if (role === 'owner' || role === 'member') {
      return (
        <div className={'animate-in fade-in flex items-center gap-x-4 duration-500'}>
          <div className={'hidden md:flex'}>
            <If condition={features.enableThemeToggle}>
              <ModeToggle />
            </If>
          </div>

          <Link
            href={paths.bookCourt}
            onClick={onNavigate}
            className="inline-flex items-center justify-center bg-[#1679FF] text-white font-conthrax text-sm h-10 px-6 rounded-full hover:bg-[#1679FF]/90 transition-colors duration-200"
          >
            <Trans i18nKey={'common:bookCourt'} defaults="Book Now" />
          </Link>

          <Link
            href={
              teamAccount.data
                ? `/home/${teamAccount.data.slug}/dashboard`
                : '/home/settings'
            }
            onClick={onNavigate}
            className="hidden md:inline-flex items-center justify-center border-2 border-[#B8EC17] text-[#B8EC17] font-conthrax text-sm h-10 px-6 rounded-full hover:bg-[#B8EC17]/10 transition-colors duration-200"
          >
            <Trans i18nKey={'common:adminPanel'} defaults="Admin Panel" />
          </Link>

          <PersonalAccountDropdown
            showProfileName={false}
            paths={paths}
            features={features}
            user={session.data.user as unknown as JWTUserData}
            signOutRequested={() => signOut.mutateAsync()}
            className="z-50"
          />
        </div>
      );
    }

    // User role - show profile only
    if (role === 'user') {
      return (
        <div className={'animate-in fade-in flex items-center gap-x-4 duration-500'}>
          <div className={'hidden md:flex'}>
            <If condition={features.enableThemeToggle}>
              <ModeToggle />
            </If>
          </div>

          <Link
            href={paths.bookCourt}
            onClick={onNavigate}
            className="inline-flex items-center justify-center bg-[#1679FF] text-white font-conthrax text-sm h-10 px-6 rounded-full hover:bg-[#1679FF]/90 transition-colors duration-200"
          >
            <Trans i18nKey={'common:bookCourt'} defaults="Book Now" />
          </Link>

          <UserAccountDropdown
            showProfileName={false}
            features={features}
            user={session.data.user as unknown as JWTUserData}
            signOutRequested={() => signOut.mutateAsync()}
            className="z-50"
          />
        </div>
      );
    }
  }

  return <AuthButtons onNavigate={onNavigate} />;
}

function AuthButtons({ onNavigate }: { onNavigate?: () => void }) {
  return (
    <div className={'animate-in fade-in flex items-center gap-x-4 duration-500'}>
      <div className={'hidden md:flex'}>
        <If condition={features.enableThemeToggle}>
          <ModeToggle />
        </If>
      </div>

      <div className={'md:hidden'}>
        <If condition={features.enableThemeToggle}>
          <MobileModeToggle />
        </If>
      </div>

      <Link
        href={paths.bookCourt}
        onClick={onNavigate}
        className="inline-flex items-center justify-center bg-[#1679FF] text-white font-conthrax text-sm h-10 px-6 rounded-full hover:bg-[#1679FF]/90 transition-colors duration-200"
      >
        <Trans i18nKey={'common:bookCourt'} defaults="Book Now" />
      </Link>

      {/* Use <a> tag for hard navigation to avoid hydration mismatch between route groups */}
      <a
        href={pathsConfig.auth.signIn}
        onClick={onNavigate}
        className="inline-flex items-center justify-center border-2 border-[#B8EC17] text-[#B8EC17] font-conthrax text-sm h-10 px-6 rounded-full hover:bg-[#B8EC17]/10 transition-colors duration-200"
      >
        <Trans i18nKey={'auth:signIn'} defaults="Sign In" />
      </a>
    </div>
  );
}
